import type { MouseEvent, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { useDraggable } from '@dnd-kit/core';
import { createReadableClass } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';

type SliderDraggableRailProps = PropsWithChildren<{
  color: string;
  onClick: ({ clientX }: MouseEvent) => void;
  ariaLabel?: string;
  ariaValueMin?: number;
  ariaValueMax?: number;
  ariaValueNow?: number;
}>;

export const SliderDraggableRail = forwardRef<
  HTMLElement,
  SliderDraggableRailProps
>(function SliderDraggableRail(
  {
    children,
    color,
    ariaLabel,
    ariaValueMax,
    ariaValueMin,
    ariaValueNow,
    ...props
  },
  forwardingRef
) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: 'slider',
    attributes: {
      role: 'slider'
    }
  });
  const mergedRef = useMergedRef(forwardingRef, setNodeRef);

  return (
    <Rail
      {...attributes}
      {...listeners}
      {...props}
      aria-label={ariaLabel || undefined}
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      aria-valuenow={ariaValueNow}
      _color={color}
      ref={mergedRef}
    >
      {children}
    </Rail>
  );
});

const Rail = styled.div.attrs<{ _color: string }>({
  className: createReadableClass({
    component: 'slider-draggable-rail'
  })
})`
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 5px;
  background: ${({ _color }) => _color};
  cursor: pointer;
`;
